import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TextInput } from '../../../input/TextInput'
import { NumberInput } from '../../../input/NumberInput'
import FormSection from '../../../formSection/FormSection'
import { SwitchInput } from '../../../input/switchInput/SwitchInput'
import { WeekdaySelector } from '../../../weekdaySelector/WeekdaySelector'
import { MultiSelectInput } from '../../../input/multiSelectInput/MultiSelectInput'
import { SelectOptionInput } from '../../../input/selectOptionInput/SelectOptionInput'
import { IntervalListInput, isValidInterval } from '../../../input/intervalListInput/IntervalListInput'
import FormSectionActions from '../../../formSection/formSectionActions/FormSectionActions'

import { PassColor, PassProduct } from '../../../../types/booking/booking'
import { ClubModelMetadata, CourtModel } from '../../../../types/court/court'
import { DayOfWeekOrder } from '../../../../utils/dateUtils'

/** A new pass for the first club, eligible on all of its courts. */
/** A new pass for the given club, eligible on all of its courts (same defaults as a new price rule). */
export const emptyPassProduct = (club?: ClubModelMetadata, courts: CourtModel[] = []): PassProduct => {
  const clubId = club?.id
  return {
    clubId,
    name: '',
    color: PassColor.DEFAULT,
    mostPopular: false,
    price: 0,
    currency: 'ron',
    includedMinutes: 600,
    validityDays: 30,
    courtIds: courts.filter(court => court.club?.id === clubId).map(court => court.id!).filter(Boolean),
    weekdays: DayOfWeekOrder,
    intervals: [{ startTime: club?.openTime?.slice(0, 5) ?? '08:00', endTime: club?.closeTime?.slice(0, 5) ?? '23:00' }],
  }
}

type PassProductFormProps = {
  initial: PassProduct
  clubs: ClubModelMetadata[]
  courts: CourtModel[]
  isEditMode: boolean
  loading: boolean
  onSave: (product: PassProduct) => void
  onCancel: () => void
}

const isPositiveInteger = (value: number) => Number.isInteger(value) && value > 0

/**
 * Commercial terms of a pass. Saving an existing product creates a new immutable version: passes already bought keep
 * the terms they were bought with, pending purchases are honoured with their version.
 */
export const PassProductForm = ({ initial, clubs, courts, isEditMode, loading, onSave, onCancel }: PassProductFormProps) => {
  const { t } = useTranslation(['passes', 'common'])
  // Book-ahead days are optional; the server rejects values below the global booking window.
  const isValidBookAhead = (days?: number | null) => days === null || days === undefined || isPositiveInteger(days)
  const [product, setProduct] = useState<PassProduct>(initial)
  const dirty = useMemo(() => JSON.stringify(product) !== JSON.stringify(initial), [product, initial])

  // A pass is sold for one club; only that club's courts can be picked (as for price rules).
  const courtOptions = courts
    .filter(court => court.club?.id === product.clubId)
    .map(court => ({ value: String(court.id), label: court.name }))

  /** Switching club selects all of the new club's courts. */
  const setClub = (clubId: string) => {
    const club = clubs.find(item => String(item.id) === clubId)
    setProduct(current => ({
      ...current,
      clubId: club?.id,
      courtIds: courts.filter(court => club !== undefined && court.club?.id === club.id).map(court => court.id!),
    }))
  }

  const valid =
    product.clubId !== undefined &&
    product.name.trim().length > 0 &&
    product.price >= 0 &&
    isPositiveInteger(product.includedMinutes) &&
    isPositiveInteger(product.validityDays) &&
    isValidBookAhead(product.bookAheadDays) &&
    product.courtIds.length > 0 &&
    product.weekdays.length > 0 &&
    product.intervals.length > 0 &&
    product.intervals.every(isValidInterval)

  const set = <K extends keyof PassProduct>(field: K, value: PassProduct[K]) =>
    setProduct(current => ({ ...current, [field]: value }))

  return (
    <FormSection
      title={t(isEditMode ? 'passes.form.edit-title' : 'passes.form.new-title')}
      subtitle={t(isEditMode ? 'passes.form.edit-subtitle' : 'passes.form.subtitle')}
      actions={
        <FormSectionActions
          isDirty={dirty && valid && !loading}
          loading={loading}
          onSave={() => onSave(product)}
          onCancel={() => (isEditMode ? setProduct(initial) : onCancel())}
          saveLabel={t('passes.form.save-btn')}
          resetLabel={t(isEditMode ? 'passes.form.reset-btn' : 'passes.form.cancel-btn')}
        />
      }
      headerAside={
        <div className="most-popular-field">
          <SwitchInput
            name="mostPopular"
            checked={Boolean(product.mostPopular)}
            label={t('passes.form.fields.most-popular')}
            disabled={loading}
            onChange={(_, checked) => set('mostPopular', checked)}
          />
        </div>
      }
    >
      <div className="row g-3 manager-form">
        <div className="col-12 col-md-6">
          <TextInput
            name="name"
            label={t('passes.form.fields.name')}
            value={product.name}
            onChange={(_, value) => set('name', value)}
            errorMessage={t('passes.validation.required')}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <div className="pass-color-field">
            <SelectOptionInput
              name="color"
              label={t('passes.form.fields.color')}
              value={product.color ?? PassColor.DEFAULT}
              list={Object.values(PassColor).map(color => ({
                value: color,
                label: t(`passes.color.${color.toLowerCase()}`),
              }))}
              onChange={(_, value) => set('color', value as PassColor)}
              required
            />
            <span
              className={`pass-color-preview pass-color-${(product.color ?? PassColor.DEFAULT).toLowerCase()}`}
              aria-hidden="true"
            />
          </div>
        </div>
        <div className="col-12 col-md-6">
          <NumberInput
            name="price"
            label={t('passes.form.fields.price')}
            value={String(product.price)}
            min={0}
            step={0.01}
            onChange={(_, value) => set('price', Number(value))}
            onValidate={value => Number(value) >= 0}
            errorMessage={t('passes.validation.non-negative')}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <NumberInput
            name="includedMinutes"
            label={t('passes.form.fields.minutes')}
            value={String(product.includedMinutes)}
            min={1}
            step={1}
            onChange={(_, value) => set('includedMinutes', Number(value))}
            onValidate={value => isPositiveInteger(Number(value))}
            errorMessage={t('passes.validation.positive-integer')}
            infoMessage={t('passes.form.fields.minutes-info')}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <NumberInput
            name="validityDays"
            label={t('passes.form.fields.validity')}
            value={String(product.validityDays)}
            min={1}
            step={1}
            onChange={(_, value) => set('validityDays', Number(value))}
            onValidate={value => isPositiveInteger(Number(value))}
            errorMessage={t('passes.validation.positive-integer')}
            required
          />
        </div>
        <div className="col-12 col-md-6">
          <NumberInput
            name="bookAheadDays"
            label={t('passes.form.fields.book-ahead')}
            value={product.bookAheadDays ? String(product.bookAheadDays) : ''}
            min={1}
            step={1}
            onChange={(_, value) => set('bookAheadDays', value.trim() ? Number(value) : null)}
            onValidate={value => !value.trim() || isValidBookAhead(Number(value))}
            errorMessage={t('passes.validation.positive-integer')}
            infoMessage={t('passes.form.fields.book-ahead-info')}
          />
        </div>
        <div className="col-12">
          <SelectOptionInput
            name="clubId"
            label={t('passes.form.fields.club')}
            value={product.clubId ? String(product.clubId) : ''}
            list={clubs.map(item => ({ value: String(item.id), label: item.name }))}
            disabled={isEditMode && Boolean(initial.clubId)}
            onChange={(_, value) => setClub(value)}
            errorMessage={t('passes.validation.club')}
            required
          />
        </div>
        <div className="col-12">
          <MultiSelectInput
            name="courtIds"
            label={t('passes.form.fields.courts')}
            value={courtOptions.filter(option => product.courtIds.includes(Number(option.value)))}
            options={courtOptions}
            onChange={(_, values) => set('courtIds', values.map(Number))}
            errorMessage={t('passes.validation.courts')}
            required
          />
        </div>
        <div className="col-12">
          <WeekdaySelector
            days={product.weekdays}
            disabled={loading}
            label={t('passes.form.fields.weekdays')}
            onChange={weekdays => set('weekdays', weekdays)}
          />
        </div>
        <div className="col-12">
          <div className="eyebrow mb-8">{t('passes.form.fields.intervals')}</div>
          <IntervalListInput
            intervals={product.intervals}
            disabled={loading}
            onChange={intervals => set('intervals', intervals)}
          />
        </div>
      </div>
    </FormSection>
  )
}
